import _ from "lodash";
import { FieldValues } from "react-hook-form";
import { toast, ToastOptions } from "react-toastify";
import { FORM_CONSTANT } from "src/constants/form.constants";
import { PROFILE } from "src/constants/profile.constants";
import sidebarItems from "src/data/sidebar.data";
import urlList from "src/data/urlList.data";
import { jobLocales } from "src/locales/jobLocales";
import { UserModule } from "src/models/user.model";
import API_SERVICES from "src/services";
import handleResponse from "src/services/apiConfig/api.response.service";

import { CONSTANTS } from "../constants";
import { FlattenObject } from "../models/layout.models";

const statusMapping = {
  [CONSTANTS.USER_TYPE.OFFICE.KEY]: CONSTANTS.USER_TYPE.OFFICE.NAME,
  [CONSTANTS.USER_TYPE.PROFESSIONAL.KEY]: CONSTANTS.USER_TYPE.PROFESSIONAL.NAME,
  [CONSTANTS.USER_TYPE.ADMIN.KEY]: CONSTANTS.USER_TYPE.ADMIN.NAME,
};
export const convertToCamelCase = (readableString: string) => {
  return readableString
    ?.toLowerCase()
    .split(CONSTANTS.COMMON_CONSTANTS.EMPTY_SPACE)
    .map((word, index) => {
      if (index === CONSTANTS.DEFAULT_VALUE) {
        return word;
      }
      return (
        word.charAt(CONSTANTS.DEFAULT_VALUE).toUpperCase() +
        word.slice(PROFILE.FIRST_ITEM)
      );
    })
    .join(CONSTANTS.COMMON_CONSTANTS.EMPTY_VALUE);
};
export function formatPhoneNumber(phoneNumber?: string) {
  if (!phoneNumber) return jobLocales.NA;
  const digits = phoneNumber.replace(
    /\D/g,
    CONSTANTS.COMMON_CONSTANTS.EMPTY_VALUE
  );
  if (digits.length !== CONSTANTS.MOBILE_WITH_CODE)
    return CONSTANTS.COMMON_CONSTANTS.EMPTY_VALUE;
  return `${digits[CONSTANTS.DEFAULT_VALUE]}-${digits.slice(
    CONSTANTS.MOBILE_INDEX.FIRST_NUMBER_OF_MOBILE_INDEX,
    CONSTANTS.MOBILE_INDEX.FOURTH_NUMBER_OF_MOBILE_INDEX
  )}-${digits.slice(
    CONSTANTS.MOBILE_INDEX.FOURTH_NUMBER_OF_MOBILE_INDEX,
    CONSTANTS.MOBILE_INDEX.SEVENTH_NUMBER_OF_MOBILE_INDEX
  )}-${digits.slice(CONSTANTS.MOBILE_INDEX.SEVENTH_NUMBER_OF_MOBILE_INDEX)}`;
}
const flattenObjectToString = function (objectToFlatten: FlattenObject) {
  const flattenedObject: { [key: string]: string } = {};
  let errorString = CONSTANTS.COMMON_CONSTANTS.EMPTY_VALUE;
  for (const key in objectToFlatten) {
    if (!Object.hasOwn(objectToFlatten, key)) continue;
    if (typeof objectToFlatten[key] === FORM_CONSTANT.OBJECT) {
      flattenedObject[key] = flattenObjectToString(
        objectToFlatten[key] as FlattenObject
      );
    } else {
      flattenedObject[key] = objectToFlatten[key] as string;
    }
  }
  for (const propertyKey in flattenedObject) {
    errorString =
      errorString === CONSTANTS.COMMON_CONSTANTS.EMPTY_VALUE
        ? errorString + flattenedObject[propertyKey]
        : errorString + ", " + flattenedObject[propertyKey];
  }
  return errorString;
};

const getProfileInfo = async (type: string) => {
  const data = await API_SERVICES.USER.PROFILE_COMMON_DATA(type);
  const responseData = handleResponse(data);
  if (responseData.ok) {
    return responseData?.data;
  }
};

const getUrlReplace = (url: string, key: string, replaceValue: string) => {
  return url.replace(key, replaceValue).replace(/[{}]/g, "");
};

const getUserType = (status: number) => statusMapping[status];

const indexToStatus = (index: number) => ++index;

const statusToIndex = (status: number) => --status;

const toastService = (
  message: string,
  type: string = CONSTANTS.TOAST_TYPE.SUCCESS,
  autoClose: false | number = CONSTANTS.TOAST_TYPE.TIMER
) => {
  const config: ToastOptions = {
    autoClose: autoClose,
    closeOnClick: true,
    draggable: true,
    hideProgressBar: false,
    pauseOnHover: true,
    position: "bottom-center",
    progress: undefined,
  };

  switch (type) {
    case CONSTANTS.TOAST_TYPE.ERROR:
      toast.error(message, { ...config });
      break;
    case CONSTANTS.TOAST_TYPE.INFO:
      toast.info(message, { ...config });
      break;
    case CONSTANTS.TOAST_TYPE.SUCCESS:
      toast.success(message, { ...config });
      break;
    case CONSTANTS.TOAST_TYPE.WARNING:
      toast.warning(message, { ...config });
      break;
  }
};
export const toSnakeCase = (str: string): string => {
  return _.snakeCase(str);
};

export const urlReplace = (url: string, key: string, value: string) => {
  const regex = new RegExp(`{${key}}`, "g");
  return url.replace(regex, value);
};

export const mbToBits = (mb: number) =>
  mb * CONSTANTS.KILO_BYTE * CONSTANTS.KILO_BYTE;

export const bitsToMb = (bytes: number) =>
  bytes / CONSTANTS.KILO_BYTE / CONSTANTS.KILO_BYTE;
export const urlPermission = (userModules: UserModule[], pathname: string) => {
  const allowedItems = userModules.map(module => module.key);
  const allowedUrls = sidebarItems
    .filter(item => !item.moduleId || allowedItems.includes(item.moduleId))
    .map(module => module?.action?.route);

  const allowedItem = urlList
    .filter(item => !item.moduleId || allowedItems.includes(item.moduleId))
    .map(module => module?.action?.route);

  const mergedAllowedUrls = [...allowedUrls, ...allowedItem];

  const permission = mergedAllowedUrls.filter(
    item => item && pathname.includes(item)
  );
  return {
    allowedUrls,
    permission,
  };
};

/**
 * Replaces placeholders in a string with values from a given object.
 *
 * The placeholders are enclosed in curly braces, e.g., {key}.
 * The function looks up the 'key' in the provided replacements object
 * and replaces the placeholder with the corresponding value.
 *
 * @param {string} str - The string containing placeholders in the format {key}.
 * @param {Object} replacements - An object containing key-value pairs to replace the placeholders.
 * @returns {string} - The resulting string with placeholders replaced by their corresponding values.
 *
 * @example
 * const template = "A {animal} was there in {place}";
 * const replacements = { animal: "cat", place: "table" };
 * const result = replacePlaceholders(template, replacements);
 * console.log(result); // Output: "A cat was there in table"
 */
export function replacePlaceholders(
  str: string,
  replacements: Record<string, string>
) {
  return str.replace(/{(.*?)}/g, (match, key) => {
    return replacements[key] || match;
  });
}

const getNameFromUUid = (uuid: string, items: FieldValues[]) => {
  return items?.find(item => item?.uuid === uuid)?.name;
};

const truncateText = (text: string, maxLength: number) => {
  return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
};

const commonUtils = {
  flattenObjectToString,
  getProfileInfo,
  getUrlReplace,
  getUserType,
  indexToStatus,
  replacePlaceholders,
  statusToIndex,
  toastService,
  urlReplace,
  urlPermission,
  getNameFromUUid,
  truncateText,
};

export default commonUtils;
